import {
	Drawer,
	DrawerContent,
	DrawerHeader,
	DrawerTitle,
	Separator,
} from "@monbits/ui";
import { useQuery } from "@tanstack/react-query";
import * as React from "react";
import { useLocation, useParams } from "react-router-dom";
import { toast } from "sonner";
import CreatePoolWithdrawalDialog from "src/components/create-pool-withdrawal-dialog";
import WalletBackupDialog from "src/components/wallet-backup-dialog/wallet-backup-dialog";
import { WalletDetailsTabs } from "src/components/wallet-details-tabs";
import walletProvider, {
	type PortfolioDetails,
} from "src/providers/wallet-provider";
import auditQueries from "src/queries/audit-queries";
import type { AuditLog } from "src/types";
import { AuditLogDrawerContent } from "@/components/audit-log-drawer";
import { EntityDrawer } from "@/components/entity-drawer";
export function WalletDetail() {
	const { walletId } = useParams<{ walletId: string }>();
	const location = useLocation();

	const [backupWalletId, setBackupWalletId] = React.useState<number | null>(
		null,
	);
	const [backupWalletAddress, setBackupWalletAddress] = React.useState("");

	const [selectedAuditLog, setSelectedAuditLog] =
		React.useState<AuditLog | null>(null);
	const [isAuditLogDrawerOpen, setIsAuditLogDrawerOpen] = React.useState(false);

	const [isWithdrawalDialogOpen, setIsWithdrawalDialogOpen] =
		React.useState(false);
	const [withdrawalPortfolio, setWithdrawalPortfolio] =
		React.useState<PortfolioDetails | null>(null);

	const parentPath = location.pathname.includes("/outbound")
		? "/wallets/outbound"
		: "/wallets/inbound";

	const { data: wallet, isLoading } = useQuery({
		queryKey: ["wallet", walletId],
		queryFn: async () => {
			if (!walletId) throw new Error("Wallet ID is required");
			return await walletProvider.getWalletDetails(Number(walletId));
		},
		enabled: !!walletId,
	});

	const walletAuditParams = React.useMemo(
		() => ({
			resourceType: "wallet",
			resourceId: walletId || "",
			limit: 50,
			offset: 0,
		}),
		[walletId],
	);

	const { data: walletAuditData, isLoading: isLoadingWalletAudit } = useQuery(
		auditQueries.listAuditLogs(
			walletId ? walletAuditParams : { limit: 0, offset: 0 },
		),
	);

	const walletAuditLogs = walletAuditData?.logs ?? [];

	const handleOpenWithdrawalDialog = React.useCallback(
		(portfolio: PortfolioDetails | null) => {
			setWithdrawalPortfolio(portfolio);
			setIsWithdrawalDialogOpen(true);
		},
		[],
	);

	const handleWithdrawalSuccess = React.useCallback(() => {
		toast.success("Withdrawal created successfully");
		setIsWithdrawalDialogOpen(false);

		if (location.pathname.includes("/outbound")) {
			const refetch = (
				window as unknown as { refetchOutboundWallets?: () => void }
			).refetchOutboundWallets;
			if (refetch) refetch();
		}
	}, [location.pathname]);

	const handleWithdrawalClose = React.useCallback(() => {
		setIsWithdrawalDialogOpen(false);
		setWithdrawalPortfolio(null);
	}, []);

	return (
		<>
			<EntityDrawer
				title="Wallet Details"
				isLoading={isLoading}
				isNotFound={!wallet && !isLoading}
				notFoundMessage="Wallet not found"
				parentPath={parentPath}
			>
				{wallet && (
					<div className="p-6">
						<WalletDetailsTabs
							wallet={wallet}
							auditLogs={walletAuditLogs}
							isLoadingAuditLogs={isLoadingWalletAudit}
							onBackupClick={() => {
								setBackupWalletId(wallet.id);
								setBackupWalletAddress(wallet.address);
							}}
							onAuditLogClick={(log) => {
								setSelectedAuditLog(log);
								setIsAuditLogDrawerOpen(true);
							}}
							onWithdrawClick={(portfolio) => {
								handleOpenWithdrawalDialog(portfolio);
							}}
						/>
					</div>
				)}
			</EntityDrawer>

			<Drawer
				open={isAuditLogDrawerOpen}
				onOpenChange={(open) => {
					setIsAuditLogDrawerOpen(open);
					if (!open) {
						setTimeout(() => {
							setSelectedAuditLog(null);
						}, 300);
					}
				}}
				direction="right"
			>
				<DrawerContent className="h-screen overflow-hidden flex flex-col w-full max-w-full sm:max-w-sm md:max-w-[600px]">
					<div className="flex flex-col h-full w-full">
						<DrawerHeader>
							<DrawerTitle>Audit Log Details</DrawerTitle>
						</DrawerHeader>

						<Separator />

						<div className="flex-1 overflow-y-auto">
							{selectedAuditLog ? (
								<AuditLogDrawerContent log={selectedAuditLog} />
							) : (
								<div className="h-full flex items-center justify-center text-sm text-muted-foreground px-6">
									Select an audit log to view its details
								</div>
							)}
						</div>
					</div>
				</DrawerContent>
			</Drawer>

			<WalletBackupDialog
				walletId={backupWalletId}
				walletAddress={backupWalletAddress}
				onClose={() => {
					setBackupWalletId(null);
					setBackupWalletAddress("");
				}}
			/>

			{wallet && (
				<CreatePoolWithdrawalDialog
					isOpen={isWithdrawalDialogOpen}
					onClose={handleWithdrawalClose}
					onSuccess={handleWithdrawalSuccess}
					walletId={wallet.id}
					walletAddress={wallet.address}
					blockchain={wallet.blockchain}
					balances={
						withdrawalPortfolio
							? [
									...(withdrawalPortfolio.native
										? [
												{
													currency: withdrawalPortfolio.native.currency,
													symbol: withdrawalPortfolio.native.symbol,
													amount: withdrawalPortfolio.native.balance,
													usdAmount: withdrawalPortfolio.native.valueUsd,
													blockchain: wallet.blockchain,
												},
											]
										: []),
									...withdrawalPortfolio.tokens.map((token) => ({
										currency: token.currency,
										symbol: token.symbol,
										amount: token.balance,
										usdAmount: token.valueUsd,
										blockchain: wallet.blockchain,
									})),
								]
							: []
					}
				/>
			)}
		</>
	);
}
